import type { CSSProperties, FC } from 'react';
import { useState, useEffect } from 'react';
import type {
  EncroachmentDetails,
  LayerConfig,
} from '@/views/map/layers/types';
import { LayerType } from '@/views/map/layers/types';
import type { Device, Framebuffer, Texture } from '@luma.gl/core';
import { useTouch } from '@/hooks/useTouch';
import classes from './InspectTooltip.module.css';
import {
  useSmallScreen,
  useWindowDimensions,
} from '@/hooks/useWindowDimension';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { useDatasets } from '@/hooks/useDatasets';
import {
  DECIDUOUS_COLOR,
  EVERGREEN_COLOR,
  EVERGREEN_COLOR_SAFARI,
  DECIDUOUS_COLOR_SAFARI,
} from '@/views/map/layers/treeType';
import { useFetchEncroachmentConfigQuery } from '@/features/analysisApi';
import type { HoverInfo } from '@/views/MapView';
import { useAppSelector } from '@/app/hooks';
import {
  WORK_TYPE_MESSAGE_IDS,
  type WorktypeAnalysisRating,
} from '@/features/types';
import { handleTreeSpecies, findBuckets, type BucketConfig } from './utils';
import {
  getWorktypeFromPixelColor,
  WTA_COLOR_MAP,
} from '@/views/map/layers/worktypeAnalysis/utils';
import { isTreeCoverActive } from '@/views/map/utils';
import { type PickingInfo } from 'deck.gl';

type PickingInfoWithImage = {
  sourceLayer: {
    props: {
      image: Framebuffer | Texture;
    };
  };
  bitmap: {
    pixel: [number, number];
  };
  layer: {
    context: {
      device: Device;
    };
  };
};

interface Props {
  isOverview: boolean;
  layerConfig: Record<LayerType, LayerConfig>;
  tooltipCoordinates: number[];
  layers?: PickingInfo[];
  hoverInfo: HoverInfo;
  setHoverInfo: (d: HoverInfo) => void;
}

const TOOLTIP_BUFFER = 10;

const tooltipPositionVars = (coords: number[]): CSSProperties =>
  ({
    '--inspect-tooltip-left': `${coords?.[0] + TOOLTIP_BUFFER}px`,
    '--inspect-tooltip-top': `${coords?.[1] + TOOLTIP_BUFFER}px`,
  }) as CSSProperties;

const hasImage = (
  info: PickingInfo
): info is PickingInfo & PickingInfoWithImage =>
  'bitmap' in info && !!info.sourceLayer && !!info.layer;

const getPixelColor = (d: PickingInfoWithImage) =>
  d.layer.context.device.readPixelsToArrayWebGL(d.sourceLayer.props.image, {
    sourceX: d.bitmap.pixel[0],
    sourceY: d.bitmap.pixel[1],
    sourceWidth: 1,
    sourceHeight: 1,
  });

export const InspectTooltip: FC<Props> = ({
  layerConfig,
  tooltipCoordinates,
  isOverview,
  hoverInfo,
  setHoverInfo,
  layers,
}: Props) => {
  const positionVars = tooltipPositionVars(tooltipCoordinates);
  const isSmallScreen = useSmallScreen();
  const isTouchDevice = useTouch();
  const { width } = useWindowDimensions();
  const intl = useClientTerminology();
  const { datasets } = useDatasets();
  const TB_BREAKPOINT = 481;
  const { isInspectMode } = useAppSelector((s) => s.analysisMap);
  const hasTabletDimensions = width > TB_BREAKPOINT && isTouchDevice;
  const [treetype, setTreetype] = useState('');
  const { data: encroachmentConfig } = useFetchEncroachmentConfigQuery(
    undefined,
    {
      skip:
        !datasets.find((d) => d.layerType === 'distance_bucket') || isOverview, // Adding this to defer getting encroachment config when its not map overview
    }
  );

  const [horizontalBoundaries, setHorizontalBoundaries] = useState<string[]>(
    []
  );
  const [verticalBoundaries, setVerticalBoundaries] = useState<string[]>([]);
  const [config, setConfig] = useState<BucketConfig[]>([]);
  const [matrixId, setMatrixId] = useState(undefined);
  const [encroachmentDetails, setEncroachmentDetails] =
    useState<EncroachmentDetails>({
      horizontalDistanceToLine: 0,
      verticalDistanceToLine: 0,
    });
  const [overhang, setOverhang] = useState(false);
  const [worktype, setWorktype] = useState<WorktypeAnalysisRating | undefined>(
    undefined
  );
  const NODATA = 255;
  const showTooltip =
    !isOverview &&
    isTreeCoverActive(layerConfig) &&
    ((![NODATA, 0].includes(encroachmentDetails.horizontalDistanceToLine) &&
      layerConfig[LayerType.distance_bucket]) ||
      overhang ||
      worktype !== undefined ||
      treetype !== '' ||
      hoverInfo?.contents !== undefined);

  const hasLayer = (layerType: LayerType) =>
    datasets.findIndex((d) => d.layerType === LayerType[layerType]) !== -1;

  const speciesContent =
    hoverInfo?.contents === 'Other deciduous'
      ? 'Deciduous'
      : hoverInfo?.contents === 'Other evergreen'
        ? 'Evergreen'
        : hoverInfo?.contents;

  const pickLayers = (layers: PickingInfo[]) => {
    setHoverInfo({}); // Clear the hover state

    const getLayer = (layerId: string) =>
      layers.find((x) => x.layer?.id === layerId);

    // Only the raster layers carry the framebuffer the colour is sampled from.
    const readPixelColor = (layerId: string) => {
      const layer = getLayer(layerId);
      return layer && hasImage(layer)
        ? Array.from(getPixelColor(layer))
        : undefined;
    };

    const treeTypeColor = readPixelColor('tree_type');
    if (treeTypeColor) {
      const pixelColor = treeTypeColor;
      setTreetype(
        pixelColor.toString() === DECIDUOUS_COLOR.toString() ||
          pixelColor.toString() === DECIDUOUS_COLOR_SAFARI.toString()
          ? 'Deciduous'
          : pixelColor.toString() === EVERGREEN_COLOR.toString() ||
              pixelColor.toString() === EVERGREEN_COLOR_SAFARI.toString()
            ? 'Evergreen'
            : ''
      );
    }
    const distanceColor = readPixelColor('distance_bucket');
    if (distanceColor) {
      setEncroachmentDetails({
        horizontalDistanceToLine: distanceColor[0] > 0 ? distanceColor[0] : -1,
        verticalDistanceToLine: distanceColor[1] > 0 ? distanceColor[1] : -1,
      });
    }

    const overhangColor = readPixelColor('overhang');
    if (overhangColor) {
      setOverhang(overhangColor[0] > 0 ? true : false);
    }

    const scan = getLayer('scan');
    if (scan?.object?.properties?.encMatrixId) {
      setMatrixId(scan.object.properties.encMatrixId);
    }

    const treeSpecies = getLayer('tree_species');
    if (treeSpecies) {
      handleTreeSpecies(treeSpecies, setHoverInfo);
    }

    const worktypeColor = readPixelColor('worktype_analysis');
    if (worktypeColor) {
      const rating = getWorktypeFromPixelColor(WTA_COLOR_MAP, worktypeColor);
      setWorktype(rating);
    }
  };

  const getBuckets = () =>
    encroachmentConfig?.map((result) => findBuckets(result)) ?? [];

  useEffect(() => {
    if (layers) {
      pickLayers(layers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers]);

  useEffect(() => {
    if (encroachmentConfig) {
      setConfig(getBuckets());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encroachmentConfig]);

  useEffect(() => {
    if (matrixId) {
      config.forEach((d) => {
        if (matrixId === d.id) {
          setHorizontalBoundaries(d.horizontalBoundaries);
          setVerticalBoundaries(d.verticalBoundaries);
        }
      });
    }
  }, [matrixId, config]);

  return showTooltip ? (
    <div
      className={`${classes.inspectTooltip} ${
        hasTabletDimensions ? classes.tooltipTablet : ''
      }`}
      style={positionVars}
      data-testid="inspect-tooltip"
    >
      {encroachmentDetails.horizontalDistanceToLine <=
        horizontalBoundaries.length &&
        horizontalBoundaries[
          encroachmentDetails.horizontalDistanceToLine - 1
        ] !== undefined && (
          <div>
            <span>
              {intl.formatMessage({
                id: 'map.tooltip.horizontalDistanceToLine',
              })}
            </span>
            <span>
              {
                horizontalBoundaries[
                  encroachmentDetails.horizontalDistanceToLine - 1
                ]
              }
            </span>
          </div>
        )}
      {encroachmentDetails.verticalDistanceToLine <=
        verticalBoundaries.length &&
        verticalBoundaries[encroachmentDetails.verticalDistanceToLine - 1] &&
        encroachmentDetails.horizontalDistanceToLine !== NODATA && (
          <div>
            <span>
              {intl.formatMessage({
                id: 'map.tooltip.verticalDistanceToLine',
              })}
            </span>
            <span>
              {
                verticalBoundaries[
                  encroachmentDetails.verticalDistanceToLine - 1
                ]
              }
            </span>
          </div>
        )}
      {overhang && (
        <div>
          <span>Overhang</span>
          <span>{overhang ? 'Yes' : 'No'}</span>
        </div>
      )}
      {hasLayer(LayerType.tree_type) &&
        treetype &&
        !hasLayer(LayerType.tree_species) && (
          <div>
            <span>Tree type</span>
            <span>{treetype}</span>
          </div>
        )}
      {hasLayer(LayerType.tree_species) && hoverInfo && hoverInfo?.contents && (
        <div>
          <span>Tree species</span>
          <span>{speciesContent}</span>
        </div>
      )}
      {worktype !== undefined && (
        <div>
          <span>{intl.formatMessage({ id: 'map.tooltip.worktype' })}</span>
          <span>
            {intl.formatMessage({ id: WORK_TYPE_MESSAGE_IDS[worktype] })}
          </span>
        </div>
      )}
    </div>
  ) : !isTreeCoverActive(layerConfig) ? (
    <div
      className={`${classes.inspectTooltip} ${
        hasTabletDimensions ? classes.tooltipTablet : ''
      } ${classes.tooltipTextAdditionalCases}`}
      style={positionVars}
      data-testid="inspect-tooltip-empty"
    >
      Enable tree cover in the Layers menu to inspect vegetation
    </div>
  ) : isOverview &&
    isInspectMode &&
    isSmallScreen &&
    isTreeCoverActive(layerConfig) ? (
    <div
      className={`${classes.inspectTooltip} ${
        classes.tooltipTextAdditionalCases
      } ${hasTabletDimensions ? classes.tooltipTablet : ''}`}
      style={positionVars}
      data-testid="inspect-tooltip-zoom"
    >
      Zoom closer to inspect vegetation
    </div>
  ) : null;
};
